/**
 * Применение итогов сессии к состоянию учебных единиц.
 *
 * Итог сессии по единице — это агрегат, а не набор наблюдений: сырые нажатия
 * на этом уровне уже не существуют (ТЗ §11.2). Отсюда единственная задача —
 * превратить агрегат в новое состояние и назначить срок повторения.
 *
 * Функция общая для всех режимов, которые учат клавишам: занятие «Основ» и
 * занятие «Цифр» отличаются материалом, но не арифметикой (docs/ARCHITECTURE.md §5).
 */

import type { MasteryStat, MasteryUnit, UnitOutcome } from '@type2x/shared-contracts';
import { emptyStat } from '@type2x/shared-contracts';

import type { LearningConfig } from './config/priority.v1.js';
import { applyObservation } from './mastery.js';
import { scheduleNextReview } from './review.js';

/**
 * Обновляет состояние единиц по итогам сессии.
 *
 * Единицы приходят из плана занятия: клавиша могла встретиться пользователю
 * впервые, и её состояние ещё не создано. Итог по единице, которой нет в
 * плане, отбрасывается — создавать состояние неизвестно чего нельзя.
 *
 * @param units учебные единицы занятия;
 * @param stats прежние состояния по идентификатору единицы;
 * @param outcomes агрегаты сессии;
 * @param config конфигурация алгоритма;
 * @param nowIso календарная метка (ISO 8601).
 */
export function applyUnitOutcomes(
  units: readonly MasteryUnit[],
  stats: Readonly<Record<string, MasteryStat>>,
  outcomes: readonly UnitOutcome[],
  config: LearningConfig,
  nowIso: string,
): MasteryStat[] {
  const nowMs = Date.parse(nowIso);
  const byId = new Map(units.map((unit) => [unit.id, unit]));

  return outcomes.flatMap((outcome) => {
    const unit = byId.get(outcome.unitId);
    if (unit === undefined) return [];

    const previous =
      stats[outcome.unitId] ?? emptyStat(unit.id, unit.language, unit.layoutId, nowIso);

    const updated = applyObservation(
      previous,
      {
        attempts: outcome.attempts,
        errors: outcome.errors,
        firstTryCorrect: Math.max(outcome.attempts - outcome.errors, 0),
        corrections: outcome.corrections,
        // Распределение задержек внутри сессии не сохраняется: у агрегата
        // есть только медиана, и оба показателя берутся от неё.
        medianLatencyMs: outcome.medianLatencyMs,
        p95LatencyMs: outcome.medianLatencyMs,
      },
      config,
      nowIso,
    );

    return [{ ...updated, nextReviewAt: scheduleNextReview(updated, config, nowMs) }];
  });
}
